import React, { useState } from 'react';
import { ConfiguratorComponent, CoolingZone } from '../Forms/types';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import clsx from 'clsx';
import { useStyles } from './styles';
import { Grid, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { getCoolingZones, getCustomData, getSelectedProductIdentifier } from '../../Reducers/App/Selectors';
import SelectorItem from './SelectorItem';
import NozzleOption from './NozzleOption';
import { selectOption } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { setCustomData } from '../../Reducers/Configurator/Actions';
import WaterTemperatureInput from './WaterTemperatureInput';

interface CoolingZoneSetupProps {
  components: ConfiguratorComponent[];
}

const CoolingZoneSetup: React.FC<CoolingZoneSetupProps> = (props) => {
  const { components } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const defaultZone = components.filter((component) => component.identifier === 'cooling_zone')[0];
  const nozzleComponents = components.filter((component) => component.identifier.includes('cone_nozzle'));
  const nozzleAmountComponents = components.filter((component) =>
    component.identifier.includes('nozzle_amount')
  );
  const coolingZones = useSelector(getCoolingZones);
  const customData = useSelector(getCustomData);
  const identifier = useSelector(getSelectedProductIdentifier);
  const defaultAmount = customData.selectedAmounts[identifier];
  const selectedCoolingZone = coolingZones.filter((zone) => zone.amount == defaultAmount);
  const [options, setOptions] = useState(
    selectedCoolingZone.length > 0 ? selectedCoolingZone[0].possibleCombinations : defaultZone.options
  );
  const [selectedAmount, setSelectedAmount] = useState(defaultAmount);

  const handleZoneChange = (zone: CoolingZone) => {
    setSelectedAmount(zone.amount);
    setOptions(zone.possibleCombinations);
    dispatch(
      setCustomData({
        selectedAmounts: {
          [identifier]: zone.amount,
        },
      })
    );
    const item = zone.possibleCombinations[0];
    dispatch(selectOption({ ...item, amount: item.amount ? 0 : 1 }, false, false, defaultZone));
  };

  return (
    <>
      <div className={classes.optionGroup}>
        <Grid container spacing={2}>
          <Grid item xs={6} container direction="column" justifyContent={'center'}>
            <Typography>{t('coolingZone.numberOfCoolingZones')}</Typography>
          </Grid>
          <Grid item xs={6} container direction="column" alignItems="flex-end">
            <ToggleButtonGroup value={selectedAmount} className={classes.buttonGroup}>
              {coolingZones.map((zone, index) => (
                <ToggleButton
                  key={`zone_${index}`}
                  value={zone.amount}
                  onClick={() => handleZoneChange(zone)}
                >
                  {zone.amount}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </div>

      <div className={clsx(classes.row, classes.groupTitle)}>
        <Typography>{t('coolingZone.coolingWaterTemperature')}</Typography>
      </div>
      <div className={classes.optionGroup}>
        {nozzleAmountComponents.map((component, index) => {
          if (index + 1 <= selectedAmount) {
            return <WaterTemperatureInput key={component.identifier} zoneNumber={index + 1} />;
          }
        })}
      </div>

      {selectedAmount > 1 && (
        <>
          <div className={clsx(classes.row, classes.groupTitle)}>
            <Typography>{t('coolingZone.division')}</Typography>
          </div>
          {options.map((option) => {
            return (
              <SelectorItem item={option} key={option.identifier} isOption={true} component={defaultZone} />
            );
          })}
        </>
      )}

      <div className={clsx(classes.row, classes.groupTitle)}>
        <Typography>{t('coolingZone.nozzles')}</Typography>
      </div>
      <div className={classes.optionGroup}>
        {nozzleComponents.map((component, index) => {
          if (index + 1 <= selectedAmount) {
            return (
              <NozzleOption
                key={component.identifier}
                component={component}
                zoneNumber={index + 1}
                defaultValue={
                  customData[component.identifier] ? customData[component.identifier] : 'nozzle_standard'
                }
              />
            );
          }
        })}
      </div>

      <div className={clsx(classes.row, classes.groupTitle)}>
        <Typography>{t('coolingZone.numberOfNozzles')}</Typography>
      </div>
      <div className={classes.optionGroup}>
        {nozzleAmountComponents.map((component, index) => {
          if (index + 1 <= selectedAmount) {
            return (
              <NozzleOption
                key={component.identifier}
                component={component}
                zoneNumber={index + 1}
                defaultValue={customData[component.identifier] ? customData[component.identifier] : 'once'}
              />
            );
          }
        })}
      </div>
    </>
  );
};

export default CoolingZoneSetup;
